import { useParams, Navigate } from "react-router-dom";
import Seo from "../components/Seo";
import { Pill, SectionHead, PrimaryLink, CTABand, PlaceholderNote } from "../components/bits";
import { USE_CASES } from "../content/pages";
import { PRODUCTS } from "../content/products";

export default function UseCaseDetail() {
  const { slug } = useParams();
  const u = USE_CASES.find((x) => x.slug === slug);
  if (!u) return <Navigate to="/use-cases" replace />;

  return (
    <>
      <Seo path={`/use-cases/${u.slug}`} title={`${u.title} Tracking`} description={u.short} />
      <section className="border-b border-border bg-brand-surface">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <p className="font-mono text-xs font-medium uppercase tracking-widest text-primary">Use case</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-semibold tracking-tight text-brand-navy sm:text-5xl">{u.title}</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-slate md:text-lg">{u.short}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <div className="max-w-3xl">
          <SectionHead kicker="The challenge" title="Why visibility breaks down here" />
          <p className="mt-6 text-base leading-relaxed text-brand-slate">{u.challenge}</p>
        </div>

        {u.images && (
          <div className="mt-12 grid gap-4 sm:grid-cols-2" data-testid={`usecase-gallery-${u.slug}`}>
            {u.images.map((img, i) => (
              <img
                key={img.url}
                src={img.url}
                alt={img.alt}
                loading={i === 0 ? "eager" : "lazy"}
                className={`w-full rounded-sm border border-border object-cover ${
                  u.images.length === 1 || (u.images.length === 3 && i === 0) ? "aspect-[21/9] sm:col-span-2" : "aspect-[16/9]"
                }`}
              />
            ))}
          </div>
        )}

        <div className="mt-16 grid gap-6 lg:grid-cols-2">
          {[
            { p: PRODUCTS.gnts1, text: u.gnts1 },
            { p: PRODUCTS.ll100, text: u.ll100 },
          ].map(({ p, text }) => (
            <article key={p.id} className="rounded-sm border border-border bg-white p-8" data-testid={`usecase-${u.slug}-${p.id}`}>
              <Pill label={p.pill.label} tone={p.pill.tone} />
              <h2 className="mt-4 text-xl font-semibold text-brand-navy">With the {p.name}</h2>
              <p className="mt-3 text-sm leading-relaxed text-brand-slate">{text}</p>
            </article>
          ))}
        </div>

        <div className="mt-16 max-w-3xl">
          <SectionHead kicker="Measure it" title="KPIs a pilot here should move" />
          <ul className="mt-8 grid gap-3 sm:grid-cols-2" data-testid={`usecase-kpis-${u.slug}`}>
            {u.kpis.map((k) => (
              <li key={k} className="rounded-sm border border-border bg-brand-surface p-4 font-mono text-sm text-brand-navy">{k}</li>
            ))}
          </ul>
          <div className="mt-8"><PlaceholderNote text={u.note} /></div>
          <div className="mt-10"><PrimaryLink to="/request-a-pilot" testid={`usecase-cta-${u.slug}`}>Discuss a {u.title.toLowerCase()} pilot</PrimaryLink></div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
